import * as Sentry from '@sentry/react';
import { Box, Card, Divider, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import DashboardTogglableLineChart from 'components/Dashboard/DashboardTogglableLineChart';
import DashboardLineChart from 'components/Dashboard/DashboardLineChart';
import { useContext, useEffect, useState } from 'react';
import ApiContext from 'contexts/apiContext';
import StoreContext from 'contexts/storeContext';
import { StoreMetrics } from 'types/storeMetrics';
import Spinner from 'common/Spinner';
import ErrorContext from 'contexts/errorContext';
import { GENERIC_SERVER_ERROR } from 'api';
import RefreshIndicator from 'common/RefreshIndicator';
import { Interval, DaysAndInterval, getDatesFromDaysAndInterval, getIntervalFromDaysAndInterval } from 'types/daysAndInterval';
import DashboardPacksTable from 'components/Dashboard/DashboardPacksTable';
import { DashboardBigNumber } from 'components/Dashboard/DashboardBigNumber';
import DashboardCategorySection from 'components/Dashboard/DashboardCategorySection';
import { useGate } from 'statsig-react';
import { STATSIG_FLAGS } from 'consts';
import DashboardTitle from 'common/DashboardTitle';
import DashboardDescription from 'components/Dashboard/DashboardDescription';
import { co2KgsToTreesPerYear } from 'utils';
import { DashboardTip } from 'components/Dashboard/DashboardTip';

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    marginBottom: theme.spacing(2),
    height: '100%',
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
}));

export default function DashboardScreen() {
  const { classes } = useStyles();
  const { api } = useContext(ApiContext);
  const { store } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);
  const [daysAndInterval, setDaysAndInterval] = useState<DaysAndInterval>([30, Interval.DAY]);
  const [storeMetrics, setStoreMetrics] = useState<StoreMetrics>();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const categoryDashboardEnabled = useGate(STATSIG_FLAGS.CATEGORY_DASHBOARD).value;

  useEffect(() => {
    const { startDate, endDate } = getDatesFromDaysAndInterval(daysAndInterval);
    const interval = getIntervalFromDaysAndInterval(daysAndInterval);
    setRefreshing(true);
    api.storeMetrics
      .list(store.id, startDate, endDate, interval)
      .then((res) => setStoreMetrics(res.data))
      .catch((err) => {
        setError({ message: GENERIC_SERVER_ERROR });
        console.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [api, store, daysAndInterval, setError]);

  const handleDaysAndIntervalChange = (event: any, value: DaysAndInterval) => {
    // not allowing to uncheck the currently selected option
    if (value !== null) {
      setDaysAndInterval(value);
    }
  };

  const trees = storeMetrics && co2KgsToTreesPerYear(storeMetrics.co2_emissions_sum.total_cumulative);

  return loading ? (
    <Spinner />
  ) : !!storeMetrics ? (
    // NOTE: negative margin is to have the top right interval toggle at the same height as the page title
    <Box sx={{ mt: '-48px' }}>
      <RefreshIndicator visible={refreshing} />
      {/* NOTE: 64 is the header's height, we need it to stick below it */}
      <Box sx={{ position: 'sticky', top: 64 + 10, mr: 2, zIndex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {/* NOTE: need to stringify the value for the equality check to work and it showing the selected value as selected */}
        <ToggleButtonGroup
          color='primary'
          value={JSON.stringify(daysAndInterval)}
          exclusive
          onChange={(event, value) => handleDaysAndIntervalChange(event, JSON.parse(value))}
          size='small'
          sx={{ mb: 1, backgroundColor: 'white' }}
          disabled={refreshing}
        >
          <ToggleButton value={JSON.stringify([7, Interval.DAY])}>7 días</ToggleButton>
          <ToggleButton value={JSON.stringify([30, Interval.DAY])}>30 días</ToggleButton>
          <ToggleButton value={JSON.stringify([6, Interval.MONTH])}>6 meses</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <DashboardTitle title='Ventas' />
            <Divider className={classes.divider} />
            <DashboardDescription>Ventas totales y cantidad de pedidos completados.</DashboardDescription>
            <DashboardDescription>
              Utiliza esta herramienta para <b>medir el desempeño de tu tienda</b> y cómo éste varía con el tiempo.
            </DashboardDescription>
            <DashboardTogglableLineChart
              labelLeft='Ventas'
              labelRight='Pedidos'
              dataLeft={storeMetrics.orders_total_sum}
              dataRight={storeMetrics.orders_count}
              unitLeft='$'
              unitRight='pedidos'
              interval={getIntervalFromDaysAndInterval(daysAndInterval)}
            />
            <DashboardTip>
              La ecuación es simple: <b>¡cuanto más publicas, más vendes!</b> Ser parte de Buen Provecho significa poder producir con mayor
              libertad, permitiéndote recuperar el costo directo del producto mientras das a conocer tu tienda a nuevos clientes.
            </DashboardTip>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <DashboardTitle title='Publicaciones' />
            <Divider className={classes.divider} />
            <DashboardDescription>Cantidad de packs publicados y porcentaje de éstos vendidos.</DashboardDescription>
            <DashboardDescription>
              Utiliza esta herramienta <b>para verificar estar publicando packs consistentemente todos los días</b> y que éstos tengan un
              buen desempeño.
            </DashboardDescription>
            <DashboardTogglableLineChart
              labelLeft='Publicaciones'
              labelRight='Porcentaje vendido'
              dataLeft={storeMetrics.published_count}
              dataRight={storeMetrics.published_sold_percentage}
              unitLeft='packs'
              unitRight='%'
              interval={getIntervalFromDaysAndInterval(daysAndInterval)}
            />
            <DashboardTip>
              Nuestros usuarios usan la app a toda hora, por lo que cuanto antes publiques, ¡más serán los que vean tu tienda!{' '}
              <b>El mejor momento para publicar nuevos packs es luego de que finaliza tu horario de retiro cada día</b>, para que estén
              disponibles durante toda la noche y día siguiente.
            </DashboardTip>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <DashboardTitle title='Packs' />
            <Divider className={classes.divider} />
            <DashboardDescription>
              Tus <b>packs más y menos vendidos</b> en este período.
            </DashboardDescription>
            <DashboardDescription>
              Utiliza esta herramienta para entender qué productos funcionan mejor y pensar cómo <b>aumentar tu ticket promedio</b>.
            </DashboardDescription>
            <Box display='flex' width='100%' alignItems='center'>
              <DashboardPacksTable daysAndInterval={daysAndInterval} />
              <Box sx={{ px: 7 }}>
                <DashboardBigNumber label='Ticket promedio' value={storeMetrics.orders_total_avg.total} unit='$' />
              </Box>
            </Box>
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <DashboardTip>
                  Si tienes productos que no se venden, <b>¡crea un pack combinado!</b> Juntar los productos menos vendidos con los más
                  vendidos en un mismo pack es una excelente estrategia para <b>aumentar tu porcentaje vendido y disminuir tus mermas.</b>
                </DashboardTip>
              </Grid>
              <Grid item xs={12} md={6}>
                <DashboardTip>
                  ¿Tienes ventas de bajo valor? <b>Arma packs con varias unidades o combinando distintos productos</b> para aumentar tu
                  ticket promedio. ¡Puedes usar las estadísticas de tu categoría como guía!
                </DashboardTip>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <DashboardTitle title='Tu impacto' />
            <Divider className={classes.divider} />
            <DashboardDescription>
              <b>Emisiones de CO₂ a la atmósfera evitadas</b> por tu tienda.
            </DashboardDescription>
            <DashboardDescription>
              Utiliza esta herramienta para <b>medir tu impacto ambiental</b> y cómo éste varía con el tiempo.
            </DashboardDescription>
            <Box display='flex' width='100%' alignItems='center' flexWrap='wrap' justifyContent='center'>
              <DashboardLineChart
                data={storeMetrics.co2_emissions_sum}
                unit='kg de CO₂'
                interval={getIntervalFromDaysAndInterval(daysAndInterval)}
              />
              <Box sx={{ px: 7, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DashboardBigNumber
                  label='Desde que te uniste a Buen Provecho'
                  value={storeMetrics.co2_emissions_sum.total_cumulative}
                  unit='kg de CO₂'
                />
                {!!trees && (
                  <>
                    <DashboardDescription>¡Es lo mismo que absorben</DashboardDescription>
                    <DashboardDescription>
                      <DashboardDescription sx={{ fontWeight: 'bold', opacity: 1 }} props={{ component: 'span' }}>
                        {trees} árboles{' '}
                      </DashboardDescription>
                      en un año!
                    </DashboardDescription>
                  </>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        {categoryDashboardEnabled && (
          <Grid item xs={12}>
            <Card className={classes.card}>
              <DashboardTitle title='En tu categoría' />
              <Divider className={classes.divider} />
              <DashboardDescription>
                Las tiendas y packs con mejor desempeño en tu categoría <b>en los últimos 30 días.</b>
              </DashboardDescription>
              <DashboardDescription>
                Utiliza esta herramienta para <b>comparar el desempeño de tu tienda</b> con otras en Buen Provecho y establecer objetivos de
                crecimiento.
              </DashboardDescription>
              <DashboardCategorySection />
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  ) : (
    <></>
  );
}
